import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, Outlet } from 'react-router-dom'
import { Home, ClipboardList, Shield, Users, Settings } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/store/authStore'
import { usePWAInstall } from '@/hooks/usePWAInstall'
import { usePermissions } from '@/hooks/usePermissions'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { hydrateFromCloud, flushStatEntries } from '@/lib/syncService'
import { restoreActiveState } from '@/lib/restoreState'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/lib/db'
import InstallBanner from './InstallBanner'
import SyncStatusBar from './SyncStatusBar'
import SetupWizard from './SetupWizard'
import type { LucideIcon } from 'lucide-react'

interface NavItem { to: string; label: string; Icon: LucideIcon; matches: (p: string) => boolean }

const FULL_NAV: NavItem[] = [
  { to: '/',          label: 'Home',     Icon: Home,         matches: (p) => p === '/' },
  { to: '/practice',  label: 'Practice', Icon: ClipboardList, matches: (p) => p === '/practice' || p.startsWith('/drills') },
  { to: '/schedule',  label: 'Gameday',  Icon: Shield,       matches: (p) => p === '/gameday' || p.startsWith('/schedule') || p.startsWith('/lineup') || p.startsWith('/game/') },
  { to: '/team',      label: 'Team',     Icon: Users,        matches: (p) => p === '/team' || p.startsWith('/roster') || p.startsWith('/stats') || p.startsWith('/player') },
  { to: '/settings',  label: 'Settings', Icon: Settings,     matches: (p) => p.startsWith('/settings') },
]

const STATISTICIAN_NAV: NavItem[] = [
  { to: '/schedule', label: 'Gameday',  Icon: Shield,  matches: (p) => p === '/gameday' || p.startsWith('/schedule') || p.startsWith('/game/') },
  { to: '/team',     label: 'Team',     Icon: Users,   matches: (p) => p === '/team' || p.startsWith('/stats') || p.startsWith('/player') },
  { to: '/settings', label: 'Settings', Icon: Settings, matches: (p) => p.startsWith('/settings') },
]

export default function AppShell() {
  const { pathname } = useLocation()
  const { user } = useAuthStore()
  const { isOnline, syncStatus, syncError, setSyncStatus, setLastSyncedAt, setSyncError, activeTeam, newTeamWizard, setNewTeamWizard } = useAppStore()
  const { isStatistician } = usePermissions()
  const navItems = isStatistician ? STATISTICIAN_NAV : FULL_NAV
  const { canInstall, promptInstall } = usePWAInstall()
  const [installDismissed, setInstallDismissed] = useState(
    () => sessionStorage.getItem('install-dismissed') === '1'
  )

  function dismissInstall() {
    sessionStorage.setItem('install-dismissed', '1')
    setInstallDismissed(true)
  }

  async function handleInstall() {
    await promptInstall()
    setInstallDismissed(true)
  }

  async function doSync() {
    setSyncStatus('syncing')
    setSyncError(null)
    const result = await hydrateFromCloud()
    setSyncStatus(result.status)
    if (result.lastSyncedAt) setLastSyncedAt(result.lastSyncedAt)
    if (result.reason && result.status === 'error') setSyncError(result.reason)
    await restoreActiveState()
  }

  const handleSyncNow = () => doSync()
  const handleForceSync = () => doSync()

  const [updateAvailable, setUpdateAvailable] = useState(false)
  const hadController = useRef(typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onControllerChange = () => {
      if (hadController.current) setUpdateAvailable(true)
      hadController.current = true
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
  }, [])

  const pendingCount = useLiveQuery(
    () => db.statEntries.filter(e => !e.syncedAt).count(),
    [],
    0
  ) ?? 0

  async function handleUploadNow() {
    setSyncStatus('syncing')
    await flushStatEntries()
    setSyncStatus('idle')
  }

  const { pullDistance, refreshing } = usePullToRefresh(async () => {
    await flushStatEntries()
    await doSync()
  })

  const showInstallBanner = canInstall && !installDismissed

  // Show wizard for first-time users (no team) or when explicitly adding a new team
  const showWizard = (!activeTeam && syncStatus !== 'syncing') || newTeamWizard
  if (showWizard) {
    return <SetupWizard canCancel={newTeamWizard} onDone={() => setNewTeamWizard(false)} />
  }

  const indicatorY = refreshing ? 16 : pullDistance > 0 ? pullDistance - 8 : -48
  const indicatorScale = refreshing ? 1 : Math.min(pullDistance / 38, 1)

  return (
    <div className="flex flex-col min-h-screen">

      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || refreshing) && (
        <div
          className="fixed inset-x-0 flex justify-center z-50 pointer-events-none"
          style={{ top: `env(safe-area-inset-top, 0px)`, transform: `translateY(${indicatorY}px)` }}
        >
          <div
            className="flex items-center gap-2 bg-white dark:bg-[#1e293b] shadow-lg rounded-full px-4 py-2 border border-[#e2e8f0] dark:border-[#334155]"
            style={{ transform: `scale(${indicatorScale})`, transition: refreshing ? 'none' : undefined }}
          >
            <div className={`w-4 h-4 border-2 border-[#2563eb] border-t-transparent rounded-full ${refreshing ? 'animate-spin' : ''}`} />
            <span className="text-xs font-semibold text-[#64748b] dark:text-[#94a3b8]">
              {refreshing ? 'Syncing…' : 'Release to sync'}
            </span>
          </div>
        </div>
      )}

      {/* App update banner */}
      {updateAvailable && (
        <div className="bg-[#2563eb] px-4 py-2.5 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold text-white">New version available</p>
          <button
            onClick={() => window.location.reload()}
            className="text-xs font-bold bg-white text-[#2563eb] px-3 py-1 rounded-full shrink-0"
          >
            Reload
          </button>
        </div>
      )}

      {/* PWA install banner */}
      {showInstallBanner && (
        <InstallBanner onInstall={handleInstall} onDismiss={dismissInstall} />
      )}

      {/* Sync / offline status */}
      {user && (
        <SyncStatusBar
          isOnline={isOnline}
          syncStatus={syncStatus}
          syncError={syncError}
          pendingCount={pendingCount}
          onSyncNow={handleSyncNow}
          onForceSync={handleForceSync}
          onUploadNow={handleUploadNow}
        />
      )}

      {/* Main content — pages manage their own bottom padding */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 bg-white dark:bg-[#1e293b] border-t border-[#e2e8f0] dark:border-[#334155] pb-[env(safe-area-inset-bottom,0px)]">
        <div className="flex justify-around max-w-lg mx-auto">
          {navItems.map(({ to, label, Icon, matches }) => {
            const active = matches(pathname)
            return (
              <Link
                key={to}
                to={to}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center py-2 px-2 text-[10px] transition-colors flex-1 min-h-[44px] justify-center ${
                  active
                    ? 'text-[var(--whi-primary)]'
                    : 'text-[#64748b] dark:text-[#94a3b8]'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="mt-0.5">{label}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
